import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  AlertTriangle,
  CalendarDays,
  Clock,
  Compass,
  Hash,
  Info,
  Moon,
  Palette,
  Sparkles,
  Star,
  Sunrise,
  Sunset,
} from 'lucide-react';
import {
  useGetDailyGeneralPredictionQuery,
  useGetDailyPanchangQuery,
  useGetDailyPersonalPredictionQuery,
  useGetDailyYogasQuery,
} from '@/store/api/DailyPredictions/dailyPredictionApi';
import {
  RASHI_INFO,
  formatDisplayDate,
  formatIstClock,
  formatTimeRange,
  getPredictionStatus,
  isPendingPrediction,
  normalizeRashi,
  resolveLuckyColor,
  splitTimingWindow,
  toParagraphs,
  todayLocal,
  varaYogaNature,
  type DailyPrediction,
  type RashiName,
  type YogaNature,
} from '@/utils/dailyPredictions';
import { getApiErrorMessage } from '@/utils/apiError';
import PredictionEmptyState from '@/components/common/PredictionEmptyState';

type DailyPredictionsViewProps =
  | {
      mode: 'personalised';
      userId: string;
      /** Shown in the header until the response names the sign. */
      rashi?: string | null;
    }
  | {
      mode: 'general';
      rashi?: string | null;
    };

// General polls every 3s while the backend says PENDING, like the other general
// prediction tabs. Personalised is a single request: the admin API answers with
// the prediction or a "not found" message.
const POLL_GENERAL_MS = 3000;

const MESSAGES = {
  waitPersonalised:
    "This user's daily personalised prediction is being prepared. Please wait a moment...",
  waitGeneral: 'This takes a little time as we analyze rashi-based predictions',
  failedGeneralTitle: "Couldn't load today's prediction",
  failedGeneral: 'Predictions are not visible, please click the Reload button.',
  reload: 'Reload Prediction',
  draftTitle: 'No prediction available',
  draft: 'No predictions available at the moment.',
  noMoonSignTitle: 'Moon sign not on record',
  noMoonSign: "This user has no moon sign on record, so a general daily prediction can't be shown.",
};

const INFO = {
  abhijit:
    'A highly auspicious midday period traditionally considered favorable for starting important work.',
  amrit:
    'A highly auspicious time period in the Panchang considered favorable for starting important or beneficial activities.',
  yoga: 'Yoga indicates the overall energy and nature of the day based on the positions of the Sun and Moon.',
  specialYogas:
    'Auspicious/inauspicious alignments formed by specific combinations of Nakshatras and weekdays.',
};

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

interface PredictionQueryState {
  data?: DailyPrediction;
  isPending: boolean;
  isError: boolean;
  /** The API's own message when the request failed. */
  errorMessage?: string;
  refetch: () => void;
}

interface RawQuery {
  currentData?: DailyPrediction;
  isUninitialized: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: unknown;
  refetch: () => unknown;
}

/**
 * RTK Query keeps the last result while refetching. A refetch (Reload, or a
 * general poll) counts as pending unless the result on screen is a final one.
 */
function toPredictionState(q: RawQuery): PredictionQueryState {
  const data = q.currentData;
  const status = data?.status?.toUpperCase();
  const isFinal = !!data && status !== 'PENDING' && status !== 'FAILED';
  return {
    data,
    isPending: q.isUninitialized || (q.isFetching && !isFinal),
    isError: q.isError,
    errorMessage: q.isError ? getApiErrorMessage(q.error) : undefined,
    refetch: () => {
      q.refetch();
    },
  };
}

/** Only the active mode's query runs; general polls only while the backend says PENDING. */
function useDailyPrediction(
  props: DailyPredictionsViewProps,
  date: string,
  generalRashi: RashiName | null
): PredictionQueryState {
  const [generalPoll, setGeneralPoll] = useState(0);

  const personal = useGetDailyPersonalPredictionQuery(
    props.mode === 'personalised' ? { userId: props.userId, date } : skipToken
  );

  const general = useGetDailyGeneralPredictionQuery(
    props.mode === 'general' && generalRashi
      ? { date, rashi: generalRashi, language: 'English' }
      : skipToken,
    { pollingInterval: generalPoll }
  );

  const nextGeneralPoll = isPendingPrediction(general.currentData) ? POLL_GENERAL_MS : 0;
  useEffect(() => setGeneralPoll(nextGeneralPoll), [nextGeneralPoll]);

  return toPredictionState(props.mode === 'personalised' ? personal : general);
}

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */

// Note: don't combine `flex items-center justify-between` here. globals.css hides
// that combination inside `.view-table-only`, which wraps the User Details page;
// rows push their value right with `ml-auto` instead.

type Accent = 'sky' | 'green' | 'red' | 'amber';

// Same tones as the Auspicious / Inauspicious / Gulikai timing rows, so an
// unknown yoga never looks good or bad by mistake.
const YOGA_TONE: Record<YogaNature, { spine: string; text: string }> = {
  auspicious: { spine: 'bg-emerald-500', text: 'text-emerald-700' },
  inauspicious: { spine: 'bg-rose-500', text: 'text-rose-700' },
  unknown: { spine: 'bg-gray-400', text: 'text-[#001B6B]' },
};

const ACCENT: Record<Accent, { border: string; badge: string; title: string; spine: string }> = {
  sky: {
    border: 'border-[#2DAAFD]/50',
    badge: 'bg-[#2DAAFD]/10 text-[#035B95]',
    title: 'text-[#001B6B]',
    spine: 'bg-[#2DAAFD]',
  },
  green: {
    border: 'border-emerald-300',
    badge: 'bg-emerald-50 text-emerald-700',
    title: 'text-emerald-800',
    spine: 'bg-emerald-500',
  },
  red: {
    border: 'border-rose-300',
    badge: 'bg-rose-50 text-rose-700',
    title: 'text-rose-800',
    spine: 'bg-rose-500',
  },
  amber: {
    border: 'border-amber-300',
    badge: 'bg-amber-50 text-amber-700',
    title: 'text-amber-800',
    spine: 'bg-amber-500',
  },
};

function Card({
  title,
  icon,
  info,
  accent = 'sky',
  className = '',
  children,
}: {
  title: string;
  icon: ReactNode;
  info?: string;
  accent?: Accent;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const a = ACCENT[accent];
  return (
    <section
      className={`flex min-w-0 flex-col rounded-xl border ${a.border} bg-white p-4 shadow-sm transition-shadow duration-300 hover:shadow-md ${className}`}
    >
      <header className="mb-3 flex items-center gap-2 border-b border-[#001B6B]/10 pb-2">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${a.badge}`}>
          {icon}
        </span>
        <h3
          className={`font-spectral text-[15px] font-semibold uppercase tracking-wide ${a.title}`}
        >
          {title}
        </h3>
        {info && <InfoTip label={title} text={info} />}
      </header>
      {children}
    </section>
  );
}

function InfoTip({ label, text }: { label: string; text: string }): JSX.Element {
  const descId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const show = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const gutter = 16;
    const width = Math.min(240, window.innerWidth - gutter * 2);
    const centred = rect.left + rect.width / 2 - width / 2;
    const left = Math.min(Math.max(centred, gutter), window.innerWidth - width - gutter);
    setPos({ top: rect.bottom + 8, left, width });
  }, []);

  const hide = useCallback(() => setPos(null), []);

  useEffect(() => {
    if (!pos) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('keydown', onKey);
    };
  }, [pos, hide]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`About ${label}`}
        aria-describedby={descId}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex rounded-full text-gray-500 transition-colors hover:text-[#082261] focus:outline-none focus-visible:text-[#082261] focus-visible:ring-1 focus-visible:ring-[#2DAAFD]"
      >
        <Info size={13} />
      </button>

      {/* Always in the DOM for screen readers; the visual copy is decorative. */}
      <span id={descId} className="sr-only">
        {text}
      </span>

      {pos &&
        createPortal(
          <span
            aria-hidden="true"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
            className="pointer-events-none fixed z-50 rounded-lg border border-[#2DAAFD]/40 bg-white px-3 py-2 font-lato text-[12px] normal-case leading-snug tracking-normal text-[#333333] shadow-lg"
          >
            {text}
          </span>,
          document.body
        )}
    </>
  );
}

/** One "start - end" window, with a repeated date collapsed into a suffix. */
function TimingWindow({ value, valueClass }: { value: string; valueClass: string }): JSX.Element {
  const { time, date } = splitTimingWindow(value);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap font-lato text-[13px] font-bold tabular-nums ${valueClass}`}
    >
      <span>{time}</span>
      {date && <span className="text-[10px] font-medium opacity-80">{date}</span>}
    </span>
  );
}

interface TimingRowProps {
  label: string;
  windows: string[];
  valueClass: string;
  spine: string;
  info?: string;
  numbered?: boolean;
}

/** Timing rows two per line where they fit; a last odd row spans the full width. */
function TimingGrid({ rows }: { rows: TimingRowProps[] }): JSX.Element {
  const shown = rows.filter((r) => r.windows.length > 0);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {shown.map((row, i) => (
        <TimingRow
          key={row.label}
          {...row}
          className={shown.length % 2 === 1 && i === shown.length - 1 ? 'sm:col-span-2' : ''}
        />
      ))}
    </div>
  );
}

function TimingRow({
  label,
  windows,
  valueClass,
  spine,
  info,
  numbered = false,
  className = '',
}: TimingRowProps & { className?: string }): JSX.Element | null {
  if (windows.length === 0) return null;
  return (
    <div
      className={`relative flex flex-wrap items-center gap-x-2 gap-y-1 overflow-hidden rounded-lg border border-gray-200 bg-[#F8FBFF] py-2 pl-3.5 pr-3 ${className}`}
    >
      <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] ${spine}`} />
      <span className="inline-flex shrink-0 items-center gap-1.5 font-lato text-[11px] font-semibold uppercase tracking-wider text-gray-600">
        {label}
        {info && <InfoTip label={label} text={info} />}
      </span>
      <span className="ml-auto flex flex-col items-end gap-0.5">
        {windows.map((w, i) => (
          <span key={`${w}-${i}`} className="inline-flex items-center gap-1">
            {numbered && windows.length > 1 && (
              <span className="font-lato text-[11px] font-semibold text-gray-500">({i + 1})</span>
            )}
            <TimingWindow value={w} valueClass={valueClass} />
          </span>
        ))}
      </span>
    </div>
  );
}

function PanchangItem({
  label,
  value,
  icon,
  info,
}: {
  label: string;
  value?: string;
  icon: ReactNode;
  info?: string;
}): JSX.Element | null {
  if (!value) return null;
  return (
    <div className="rounded-lg border border-[#2DAAFD]/30 bg-[#F8FBFF] p-3">
      <div className="mb-1 flex items-center gap-1.5 font-spectral text-[12px] font-semibold uppercase tracking-wider text-[#035B95]">
        {icon}
        {label}
        {info && <InfoTip label={label} text={info} />}
      </div>
      <p className="font-lato text-[15px] leading-snug text-[#333333]">{value}</p>
    </div>
  );
}

function StatTile({
  label,
  icon,
  value,
  valueClass = 'text-[#001B6B]',
}: {
  label: string;
  icon: ReactNode;
  value?: string;
  valueClass?: string;
}): JSX.Element | null {
  if (!value) return null;
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-[#2DAAFD]/40 bg-gradient-to-br from-[#F8FBFF] to-[#E6F3FF] p-3 text-center">
      <span className="flex items-center gap-1.5 font-spectral text-[11px] font-semibold uppercase tracking-wider text-gray-600">
        {icon}
        {label}
      </span>
      <span className={`font-lato text-[15px] font-bold tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

function LuckyTile({
  label,
  icon,
  children,
}: {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#2DAAFD]/50 bg-gradient-to-br from-[#F8FBFF] to-[#E6F3FF] px-4 py-3">
      <span className="flex shrink-0 items-center gap-1.5 font-spectral text-[14px] font-semibold uppercase tracking-wide text-[#001B6B]">
        {icon}
        {label}
      </span>
      <div className="ml-auto">{children}</div>
    </div>
  );
}

function WaitMessage({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p
      role="status"
      aria-live="polite"
      className="mx-auto max-w-2xl animate-pulse px-4 py-10 text-center font-spectral text-lg text-[#082261]"
    >
      {children}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* View                                                                */
/* ------------------------------------------------------------------ */

export default function DailyPredictionsView(props: DailyPredictionsViewProps): JSX.Element {
  const isPersonalised = props.mode === 'personalised';
  const [date] = useState(() => todayLocal());

  const propRashi = normalizeRashi(props.rashi);
  const generalRashi = isPersonalised ? null : propRashi;

  const panchangQuery = useGetDailyPanchangQuery(date);
  const yogaQuery = useGetDailyYogasQuery(date);
  const prediction = useDailyPrediction(props, date, generalRashi);

  if (!isPersonalised && !generalRashi) {
    return (
      <PredictionEmptyState
        title={MESSAGES.noMoonSignTitle}
        description={MESSAGES.noMoonSign}
        icon={<Moon size={22} />}
      />
    );
  }

  const status = getPredictionStatus(prediction);

  if (panchangQuery.isLoading || yogaQuery.isLoading || status === 'PENDING') {
    return (
      <WaitMessage>{isPersonalised ? MESSAGES.waitPersonalised : MESSAGES.waitGeneral}</WaitMessage>
    );
  }

  // Personalised: no Reload button; the API's own message (e.g. "Prediction
  // not generated yet") is shown as the reason.
  if (status === 'FAILED' && isPersonalised) {
    return (
      <PredictionEmptyState
        title="Today's prediction isn't ready yet"
        description={
          <>
            The daily personalised prediction for this user hasn't been generated for{' '}
            <span className="font-bold text-[#34065c]">{formatDisplayDate(date)}</span>. It
            will appear here once it's ready.
          </>
        }
        detail={prediction.errorMessage}
      />
    );
  }

  if (status === 'FAILED') {
    return (
      <PredictionEmptyState
        title={MESSAGES.failedGeneralTitle}
        description={MESSAGES.failedGeneral}
        icon={<AlertTriangle size={22} />}
        action={
          <button
            type="button"
            onClick={prediction.refetch}
            className="mt-1 rounded-lg bg-[#082261] px-6 py-2 font-spectral text-white transition-colors hover:bg-[#34065c]"
          >
            {MESSAGES.reload}
          </button>
        }
      />
    );
  }

  if (status === 'DRAFT') {
    return <PredictionEmptyState title={MESSAGES.draftTitle} description={MESSAGES.draft} />;
  }

  const data = prediction.data;
  const panchang = panchangQuery.data;
  const specialYogas = yogaQuery.data ?? [];
  const varaYogas = panchang?.varaYoga ?? [];

  const rashi = normalizeRashi(data?.rashi) ?? propRashi;
  const rashiInfo = rashi ? RASHI_INFO[rashi] : null;
  const paragraphs = toParagraphs(data?.text);
  const luckyNumbers = data?.luckyNumbers ?? [];
  const luckyColors = data?.luckyColors ?? [];

  const hasAuspicious =
    !!panchang && (panchang.abhijitMuhurat.length > 0 || panchang.amritKalam.length > 0);
  const hasInauspicious =
    !!panchang &&
    (panchang.rahuKalam.length > 0 ||
      panchang.yamaganda.length > 0 ||
      panchang.durMuhurtam.length > 0 ||
      panchang.varjyam.length > 0);
  const hasNeutral = !!panchang && panchang.gulikaKalam.length > 0;
  const hasPanchangDetails = !!(panchang?.tithi || panchang?.nakshatra || panchang?.yoga);
  const hasAlmanac = !!(
    panchang?.sunrise ||
    panchang?.sunset ||
    panchang?.amantaMonth ||
    panchang?.paksha
  );

  const almanacTiles = hasAlmanac ? (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
      <StatTile
        label="Sunrise"
        icon={<Sunrise size={13} className="text-amber-500" />}
        value={panchang?.sunrise && formatIstClock(panchang.sunrise)}
      />
      <StatTile
        label="Sunset"
        icon={<Sunset size={13} className="text-orange-500" />}
        value={panchang?.sunset && formatIstClock(panchang.sunset)}
        valueClass="text-orange-700"
      />
      <StatTile
        label="Amanta Month"
        icon={<CalendarDays size={13} className="text-[#2DAAFD]" />}
        value={panchang?.amantaMonth}
      />
      <StatTile
        label="Paksha"
        icon={<Moon size={13} className="text-amber-500" />}
        value={panchang?.paksha}
        valueClass="text-amber-800"
      />
    </div>
  ) : null;

  return (
    <section className="mx-auto w-full max-w-[1600px] py-2 text-black">
      {/* Header */}
      <div className="mb-6 flex flex-col items-center gap-4 rounded-xl border border-[#2DAAFD] bg-gradient-to-br from-[#F8FBFF] to-[#E6F3FF] p-4 sm:flex-row sm:p-5">
        <div className="flex min-w-0 items-center gap-4">
          {rashiInfo && (
            <span
              aria-hidden="true"
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(to_right,#082261,#34065c)] text-[28px] leading-none text-white shadow-md"
            >
              {`${rashiInfo.glyph}︎`}
            </span>
          )}
          <div className="min-w-0">
            <h2 className="font-spectral text-[22px] font-bold uppercase tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-[#082261] to-[#34065c]">
              {rashiInfo ? (
                <>
                  {rashiInfo.en} / <span className="normal-case">{rashiInfo.devanagari}</span>
                </>
              ) : (
                'Moon sign not on record'
              )}
            </h2>
            <p className="font-lato text-sm font-medium text-gray-600">
              {isPersonalised ? 'Personalized Predictions' : 'Daily Predictions'}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#2DAAFD]/40 bg-white sm:ml-auto px-4 py-1.5 font-lato text-sm font-semibold text-[#34065c] shadow-sm">
          <CalendarDays size={15} className="text-[#2DAAFD]" />
          {formatDisplayDate(panchang?.date ?? date)}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[9fr_11fr]">
        {/* Left column */}
        <div className="flex min-w-0 flex-col gap-4">
          <LuckyTile
            label={luckyNumbers.length > 1 ? 'Lucky Numbers' : 'Lucky Number'}
            icon={<Hash size={15} className="text-[#2DAAFD]" />}
          >
            <span className="text-right font-lato text-lg font-bold tabular-nums text-[#34065c]">
              {luckyNumbers.length ? luckyNumbers.join(', ') : '—'}
            </span>
          </LuckyTile>

          <LuckyTile
            label={luckyColors.length > 1 ? 'Lucky Colours' : 'Lucky Colour'}
            icon={<Palette size={15} className="text-[#2DAAFD]" />}
          >
            {luckyColors.length ? (
              <span className="flex flex-col items-end gap-1">
                {luckyColors.map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-2 text-right font-lato text-[15px] font-semibold text-[#34065c]"
                  >
                    {label}
                    <span
                      aria-hidden="true"
                      style={{ backgroundColor: resolveLuckyColor(label) }}
                      className="h-4 w-4 shrink-0 rounded-full border border-gray-300 shadow-inner"
                    />
                  </span>
                ))}
              </span>
            ) : (
              <span className="font-lato text-lg font-bold text-[#34065c]">—</span>
            )}
          </LuckyTile>

          {paragraphs.length > 0 && (
            <Card title="Today's Prediction" icon={<Sparkles size={16} />}>
              <div className="space-y-3">
                {paragraphs.map((p, i) => (
                  <p key={i} className="font-lato text-[17px] leading-relaxed text-[#333333]">
                    {p}
                  </p>
                ))}
              </div>
            </Card>
          )}

          {/* Desktop: at the bottom of the left column. */}
          {almanacTiles && <div className="hidden lg:block">{almanacTiles}</div>}
        </div>

        {/* Right column */}
        <div className="flex min-w-0 flex-col gap-4">
          {hasPanchangDetails && (
            <Card title="Daily Panchang Details" icon={<Compass size={16} />}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <PanchangItem label="Tithi" value={panchang?.tithi} icon={<Moon size={12} />} />
                <PanchangItem
                  label="Nakshatra"
                  value={panchang?.nakshatra}
                  icon={<Star size={12} />}
                />
                <PanchangItem
                  label="Yoga"
                  value={panchang?.yoga}
                  icon={<Sparkles size={12} />}
                  info={INFO.yoga}
                />
              </div>
            </Card>
          )}

          {specialYogas.length > 0 && (
            <Card
              title="Special Yogas"
              icon={<Sparkles size={16} />}
              accent="amber"
              info={INFO.specialYogas}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {specialYogas.map((y, i) => (
                  <div
                    key={y.id ?? `${y.yoga}-${i}`}
                    className="rounded-lg border border-amber-200 bg-amber-50/60 p-3"
                  >
                    <p className="font-spectral text-[15px] font-semibold text-amber-800">
                      {y.yoga}
                    </p>
                    {y.combination && (
                      <p className="font-lato text-[13px] text-gray-700">
                        Combination: {y.combination}
                      </p>
                    )}
                    <p className="mt-1 flex items-center gap-1 font-lato text-[13px] font-semibold tabular-nums text-amber-800">
                      <Clock size={12} className="shrink-0" />
                      {formatTimeRange(y.startTime, y.endTime)}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {hasAuspicious && panchang && (
            <Card title="Auspicious Timings (Muhurta)" icon={<Star size={16} />} accent="green">
              <TimingGrid
                rows={[
                  {
                    label: 'Abhijit Muhurat',
                    windows: panchang.abhijitMuhurat,
                    valueClass: 'text-emerald-700',
                    spine: 'bg-emerald-500',
                    info: INFO.abhijit,
                  },
                  {
                    label: 'Amrit Kalam',
                    windows: panchang.amritKalam,
                    valueClass: 'text-[#7A5A00]',
                    spine: 'bg-[#B8860B]',
                    info: INFO.amrit,
                  },
                ]}
              />
            </Card>
          )}

          {hasNeutral && panchang && (
            <TimingRow
              label="Gulikai Kalam (Neutral)"
              windows={panchang.gulikaKalam}
              valueClass="text-[#001B6B]"
              spine="bg-gray-400"
            />
          )}

          {varaYogas.length > 0 && (
            <Card title="Nakshatra and Vara Combination Yogas" icon={<Sparkles size={16} />}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {varaYogas.map((v, i) => {
                  const tone = YOGA_TONE[varaYogaNature(v.yoga)];
                  return (
                    <div
                      key={v.id ?? `${v.yoga}-${i}`}
                      className="relative overflow-hidden rounded-lg border border-gray-200 bg-[#F8FBFF] py-3 pl-4 pr-3"
                    >
                      <span
                        aria-hidden="true"
                        className={`absolute inset-y-0 left-0 w-[3px] ${tone.spine}`}
                      />
                      <p className={`font-spectral text-[15px] font-semibold ${tone.text}`}>
                        {v.yoga} Yoga
                      </p>
                      {(v.nakshatra || v.weekday) && (
                        <p className="font-lato text-[13px] text-gray-700">
                          {[v.nakshatra, v.weekday].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      <p
                        className={`mt-1 flex items-center gap-1 font-lato text-[13px] font-semibold tabular-nums ${tone.text}`}
                      >
                        <Clock size={12} className="shrink-0" />
                        {formatTimeRange(v.from, v.to)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {hasInauspicious && panchang && (
            <Card
              title="Inauspicious Timings (Caution)"
              icon={<AlertTriangle size={16} />}
              accent="red"
            >
              <TimingGrid
                rows={[
                  {
                    label: 'Rahu Kalam',
                    windows: panchang.rahuKalam,
                    valueClass: 'text-rose-700',
                    spine: 'bg-rose-500',
                  },
                  {
                    label: 'Yamaganda',
                    windows: panchang.yamaganda,
                    valueClass: 'text-rose-700',
                    spine: 'bg-rose-500',
                  },
                  {
                    label: 'Dur Muhurtam',
                    windows: panchang.durMuhurtam,
                    valueClass: 'text-rose-700',
                    spine: 'bg-rose-500',
                    numbered: true,
                  },
                  {
                    label: 'Varjyam',
                    windows: panchang.varjyam,
                    valueClass: 'text-rose-700',
                    spine: 'bg-rose-500',
                  },
                ]}
              />
            </Card>
          )}
        </div>

        {/* Single column: the almanac tiles come last. */}
        {almanacTiles && <div className="lg:hidden">{almanacTiles}</div>}
      </div>
    </section>
  );
}
